// Send-to-customer dialog — attaches the PDF and posts via Brevo SMTP.
// Extracted from QuoteBuilder.jsx (Wave K refactor).
import { proformaRef } from "./ProformaIssue";
import { useEffect, useState } from "react";
import { Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { T2Label } from "../_t2";

export default function SendQuoteDialog({ open, onClose, quote, onSent }) {
  const pf = quote?.QuoteVariant === "proforma";
  const cust = quote?.Customer || {};
  const initialTo = cust?.email || "";
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [markSent, setMarkSent] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(cust?.email || "");
    setCc("");
    setSubject(pf ? `Pro forma invoice ${proformaRef(quote?.QuoteRef)} from Construction Warehouse` : `Your quote from Construction Warehouse — ${quote?.QuoteRef || ""}`);
    setMessage("");
    setMarkSent((quote?.Status || "Draft") === "Draft");
  }, [open, cust?.email, quote?.QuoteRef, quote?.Status, pf]);

  if (!open || !quote) return null;
  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/quotes/${quote._id}/email`, {
        to: to.trim(),
        cc: cc.trim() || null,
        subject: subject.trim() || null,
        message: message.trim() || null,
        mark_sent: markSent,
      });
      if (data.demo) {
        toast.success(`Demo only — nothing sent. Email to ${data.to} prepared with “${data.attachment_filename}” (${Math.round(data.attachment_bytes / 1024)} KB).`, { duration: 7000 });
        onSent?.();
      } else {
        toast.success(`Sent to ${data.to}${data.cc ? ` (cc ${data.cc})` : ""}`);
        onSent?.();
      }
      onClose();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Email failed"));
    } finally { setSending(false); }
  };

  return (
    <div className="t2-cmd-overlay" onClick={onClose} data-testid="send-quote-overlay">
      <div className="t2-cmd" style={{ width: "min(560px, 94vw)" }} onClick={(e) => e.stopPropagation()} data-testid="send-quote-dialog">
        <div className="t2-cmd-search" style={{ padding: "12px 16px" }}>
          <Send className="w-4 h-4" style={{ color: "var(--t2-orange-ink)" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: "var(--t2-white)", fontWeight: 700 }}>{pf ? "Email pro forma invoice to customer · Demo" : "Email quote to customer · Demo"}</div>
            <div className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)" }}>
              PDF · {pf ? proformaRef(quote.QuoteRef) : quote.QuoteRef} · £{Number(quote?.Totals?.total || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })} inc VAT
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }} data-testid="send-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <T2Label>To *</T2Label>
            <input className="t2-input" type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com" data-testid="send-to" autoFocus />
          </div>
          <div>
            <T2Label>CC</T2Label>
            <input className="t2-input" type="email" value={cc} onChange={(e) => setCc(e.target.value)}
              placeholder="Optional — your team member" data-testid="send-cc" />
          </div>
          <div>
            <T2Label>Subject</T2Label>
            <input className="t2-input" value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="send-subject" />
          </div>
          <div>
            <T2Label>Message (optional)</T2Label>
            <textarea className="t2-input" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Leave blank for our default cover note…" data-testid="send-message" />
          </div>
          <div className="t2-mono" style={{ fontSize: 10.5, padding: "8px 10px", borderRadius: 6, border: "1px solid rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.08)", color: "#fcd34d" }} data-testid="send-demo-note">
            DEMO MODE — this prepares the email and the new-design PDF attachment. Nothing is sent to the customer.{pf ? " You'll then be asked whether to mark this pro forma Accepted (not paid)." : " The quote status does not change."}
          </div>
        </div>
        <div className="t2-cmd-foot" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="t2-btn-ghost" onClick={onClose} disabled={sending} data-testid="send-cancel">
            Cancel
          </button>
          <button className="t2-btn-primary" onClick={send} disabled={sending} data-testid="send-confirm">
            {sending ? <><Search className="w-3 h-3 animate-spin" /> Preparing…</> : <><Send className="w-3.5 h-3.5" /> Prepare email (demo)</>}
          </button>
        </div>
      </div>
    </div>
  );
}
